import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiFileTextLine,
  RiTvLine,
  RiPriceTag3Line,
  RiMoneyDollarCircleLine,
  RiLayoutLine,
  RiMapPinLine,
  RiCheckboxCircleLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/media-rate`;
const MEDIA_URL = `${process.env.REACT_APP_API_URL}/media/get-all`;
const ADV_TYPE_URL = `${process.env.REACT_APP_API_URL}/quotation-advertising-type`;

const defaultForm = {
  media: "",
  advertisingTypes: [], // Changed from advertisingType to advertisingTypes (array)
  edition: "",
  ratePerSquareMeter: "",
};

const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const SelectField = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder,
  Icon,
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
      {label}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
      )}
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
      >
        <option value="">{placeholder || `Select ${label}`}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  </div>
);

// New Multi-Select Component for Advertising Types
const MultiSelectField = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder,
  Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange({ target: { name, value: newValue } });
  };

  const removeOption = (optionValue) => {
    onChange({
      target: { name, value: value.filter((v) => v !== optionValue) },
    });
  };

  const selectedLabels = value
    .map((v) => {
      const option = options.find((o) => o.value === v);
      return option ? option.label : "";
    })
    .filter(Boolean);

  return (
    <div className="flex flex-col gap-1.5" ref={dropdownRef}>
      <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-3 w-4 h-4 text-blue-300 pointer-events-none z-10" />
        )}
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 cursor-pointer min-h-[42px]"
        >
          {selectedLabels.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {selectedLabels.map((label, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md text-xs"
                >
                  {label}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const optionValue = value[idx];
                      removeOption(optionValue);
                    }}
                    className="hover:text-blue-900"
                  >
                    <RiCloseLine className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className="text-blue-300">
              {placeholder || `Select ${label}`}
            </span>
          )}
        </div>

        {isOpen && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400">
                No options available
              </div>
            ) : (
              options.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(option.value)}
                    onChange={() => toggleOption(option.value)}
                    className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-slate-700">{option.label}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const InputField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  Icon,
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
      {label}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder || label}
        className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
      />
    </div>
  </div>
);

export default function MediaRate() {
  const [rates, setRates] = useState([]);
  const [mediaList, setMediaList] = useState([]);
  const [advTypes, setAdvTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchRates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(BASE_URL);
      setRates(res.data?.data || res.data || []);
    } catch {
      notify("Failed to fetch rate cards", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [mRes, aRes] = await Promise.all([
        axios.get(MEDIA_URL),
        axios.get(ADV_TYPE_URL),
      ]);
      setMediaList(mRes.data?.data || mRes.data || []);
      setAdvTypes(aRes.data?.data || aRes.data || []);
    } catch {
      notify("Failed to load dropdown data", "error");
    }
  };

  useEffect(() => {
    fetchRates();
    fetchDropdowns();
  }, []);

  const mediaOptions = mediaList.map((m) => ({
    value: m._id,
    label: m.organisation || m.name || m._id,
  }));

  const advTypeOptions = advTypes.map((a) => ({
    value: a._id,
    label: a.name,
  }));

  // All rate cards that already exist for the currently selected media
  const existingMediaRates = form.media
    ? rates.filter((r) => (r.media?._id || r.media) === form.media)
    : [];

  // Advertising type IDs already used by this media
  const existingAdvTypeIds = existingMediaRates
    .map((r) => r.advertisingType?._id || r.advertisingType)
    .filter(Boolean);

  // In edit mode, keep the rate's own current advertising type selectable
  const currentEditAdvTypeId =
    modal === "edit"
      ? selected?.advertisingType?._id || selected?.advertisingType
      : null;

  // Only offer advertising types this media doesn't already have
  // (except the one currently being edited, which stays visible/selectable)
  const remainingAdvTypeOptions = advTypeOptions.filter(
    (o) =>
      o.value === currentEditAdvTypeId || !existingAdvTypeIds.includes(o.value),
  );

  // Names of advertising types this media already has, for display
  const existingAdvTypeNames = existingMediaRates
    .map((r) => r.advertisingType?.name)
    .filter(Boolean);

  const openCreate = () => {
    setForm(defaultForm);
    setModal("create");
  };
  const openEdit = (rate) => {
    setSelected(rate);
    setForm({
      media: rate.media?._id || rate.media || "",
      advertisingTypes: [
        rate.advertisingType?._id || rate.advertisingType || "",
      ], // Convert to array for edit
      edition: rate.edition || "",
      ratePerSquareMeter: rate.ratePerSquareMeter ?? "",
    });
    setModal("edit");
  };
  const openDelete = (rate) => {
    setSelected(rate);
    setModal("delete");
  };
  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    const { media, advertisingTypes, edition, ratePerSquareMeter } = form;
    if (
      !media ||
      !advertisingTypes ||
      advertisingTypes.length === 0 ||
      !edition ||
      ratePerSquareMeter === ""
    ) {
      notify(
        "All fields are required. Please select at least one advertising type.",
        "error",
      );
      return false;
    }
    if (ratePerSquareMeter <= 0) {
      notify("Rate per square meter must be greater than 0", "error");
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setActionLoading(true);
    try {
      // Using the bulk create endpoint
      const response = await axios.post(`${BASE_URL}/bulk`, {
        media: form.media,
        advertisingTypes: form.advertisingTypes, // Send array of advertising types
        edition: form.edition,
        ratePerSquareMeter: Number(form.ratePerSquareMeter),
      });

      notify(
        response.data?.message ||
          `${response.data?.count || form.advertisingTypes.length} rate cards created successfully`,
      );
      fetchRates();
      closeModal();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to create rate cards",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    setActionLoading(true);
    try {
      const selectedTypes = form.advertisingTypes;

      // If the rate's original advertising type is still selected, keep updating
      // that record. Otherwise, fall back to the first selected type.
      const keepCurrent = selectedTypes.includes(currentEditAdvTypeId);
      const updateTypeId = keepCurrent
        ? currentEditAdvTypeId
        : selectedTypes[0];

      // Any other selected types are newly added -> create as new rate cards
      const newTypes = selectedTypes.filter((id) => id !== updateTypeId);

      // 1. Update the existing rate card
      await axios.put(`${BASE_URL}/${selected._id}`, {
        media: form.media,
        advertisingType: updateTypeId,
        edition: form.edition,
        ratePerSquareMeter: Number(form.ratePerSquareMeter),
      });

      // 2. Create new rate cards for any additionally selected advertising types
      if (newTypes.length > 0) {
        await axios.post(`${BASE_URL}/bulk`, {
          media: form.media,
          advertisingTypes: newTypes,
          edition: form.edition,
          ratePerSquareMeter: Number(form.ratePerSquareMeter),
        });
      }

      notify(
        newTypes.length > 0
          ? `Rate card updated and ${newTypes.length} new rate card${
              newTypes.length > 1 ? "s" : ""
            } added`
          : "Rate card updated successfully",
      );
      fetchRates();
      closeModal();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to update rate card",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/${selected._id}`);
      notify("Rate card deleted successfully");
      fetchRates();
      closeModal();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to delete rate card",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = rates.filter((r) => {
    const mediaName = r.media?.organisation || r.media?.name || "";
    const advName = r.advertisingType?.name || "";
    const q = search.toLowerCase();
    return (
      mediaName.toLowerCase().includes(q) ||
      advName.toLowerCase().includes(q) ||
      (r.edition || "").toLowerCase().includes(q)
    );
  });

  const tableHeaders = [
    { label: "#" },
    { label: "Media", Icon: RiTvLine },
    { label: "Adv. Type", Icon: RiPriceTag3Line },
    { label: "Edition", Icon: RiMapPinLine },
    { label: "Rate / Sq. Meter (₹)", Icon: RiMoneyDollarCircleLine },
    { label: "Created At", Icon: RiFileTextLine },
    { label: "Actions" },
  ];

  // Rendered as a plain JSX expression (not a component) to prevent
  // unmount/remount on every keystroke, which would steal focus.
  const renderFormBody = () => (
    <div className="flex flex-col gap-4">
      <SelectField
        label="Media"
        name="media"
        value={form.media}
        onChange={handleChange}
        options={mediaOptions}
        placeholder="Select Media"
        Icon={RiTvLine}
      />

      {/* Show advertising types this media already has rate cards for */}
      {form.media && existingAdvTypeNames.length > 0 && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
          <p className="text-xs font-semibold text-amber-700 mb-1.5 flex items-center gap-1">
            <RiPriceTag3Line className="w-3.5 h-3.5" />
            This media already has rate cards for:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {existingAdvTypeNames.map((name, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 rounded-md text-xs font-medium"
              >
                {name}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-amber-600 mt-1.5">
            Choose from the remaining advertising types below to add new rate
            cards for this media.
          </p>
        </div>
      )}

      <MultiSelectField
        label="Advertising Types (Select Multiple)"
        name="advertisingTypes"
        value={form.advertisingTypes}
        onChange={handleChange}
        options={remainingAdvTypeOptions}
        placeholder="Select one or more advertising types"
        Icon={RiPriceTag3Line}
      />
      <InputField
        label="Edition"
        name="edition"
        value={form.edition}
        onChange={handleChange}
        placeholder="e.g. Mumbai, Delhi, National"
        Icon={RiMapPinLine}
      />
      <InputField
        label="Rate Per Square Meter (₹)"
        name="ratePerSquareMeter"
        value={form.ratePerSquareMeter}
        onChange={handleChange}
        type="number"
        placeholder="e.g. 5000"
        Icon={RiMoneyDollarCircleLine}
      />

      {/* Show summary if multiple advertising types are selected */}
      {form.advertisingTypes.length > 1 && (
        <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1">
            <RiCheckboxCircleLine className="w-3.5 h-3.5" />
            Will create {form.advertisingTypes.length} separate rate cards:
          </p>
          <p className="text-xs text-blue-600">
            One for each selected advertising type with the same media, edition,
            and rate
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiMoneyDollarCircleLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Media Rate Cards
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Rate Card Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search rates..."
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-52 transition"
              />
            </div>

            <button
              onClick={fetchRates}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>

            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add Rate Cards
            </button>
          </div>
        </div>
      </header>

      {/* Table */}
      <div className="w-full mx-auto px-6 pt-6 pb-10">
        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiMoneyDollarCircleLine className="w-4 h-4 text-blue-500" />
              All Rate Cards
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {tableHeaders.map(({ label, Icon }) => (
                    <th key={label} className="px-4 py-3 text-left">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading rate cards…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiMoneyDollarCircleLine className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search
                            ? "No rate cards match your search."
                            : "No rate cards found. Add one to get started."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((rate, i) => (
                    <tr
                      key={rate._id || i}
                      className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                    >
                      <td className="px-4 py-3 text-slate-400 text-xs font-medium">
                        {i + 1}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold shadow-sm shadow-blue-200 flex-shrink-0">
                            {(rate.media?.organisation ||
                              rate.media?.name ||
                              "?")[0].toUpperCase()}
                          </div>
                          {rate.media?.organisation || rate.media?.name || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-xs font-semibold text-blue-600">
                          <RiPriceTag3Line className="w-3 h-3" />
                          {rate.advertisingType?.name || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {rate.edition || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-semibold text-emerald-700">
                          ₹
                          {Number(rate.ratePerSquareMeter).toLocaleString(
                            "en-IN",
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                        {rate.createdAt
                          ? new Date(rate.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEdit(rate)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                          >
                            <RiEditLine className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => openDelete(rate)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                          >
                            <RiDeleteBinLine className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {modal === "create" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Rate Cards
              (Bulk)
            </>
          }
          onClose={closeModal}
        >
          {renderFormBody()}
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Create{" "}
              {form.advertisingTypes.length > 0
                ? `(${form.advertisingTypes.length})`
                : ""}{" "}
              Rate Card{form.advertisingTypes.length !== 1 ? "s" : ""}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === "edit" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit —{" "}
              {selected?.media?.organisation ||
                selected?.media?.name ||
                "Rate Card"}
            </>
          }
          onClose={closeModal}
        >
          {renderFormBody()}
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === "delete" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete rate card for{" "}
              <span className="text-red-500">
                {selected?.media?.organisation ||
                  selected?.media?.name ||
                  "this entry"}
              </span>
              ?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. This rate card will be permanently
              removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Rate
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// import React, { useState, useEffect } from "react";
// import { createPortal } from "react-dom";
// import axios from "axios";
// import {
//   RiAddLine,
//   RiSearchLine,
//   RiCloseLine,
//   RiEditLine,
//   RiDeleteBinLine,
//   RiCheckLine,
//   RiErrorWarningLine,
//   RiRefreshLine,
//   RiFileTextLine,
//   RiTvLine,
//   RiPriceTag3Line,
//   RiMoneyDollarCircleLine,
//   RiLayoutLine,
//   RiMapPinLine,
//   RiCheckboxCircleLine,
// } from "react-icons/ri";
// import { AiOutlineLoading3Quarters } from "react-icons/ai";

// const BASE_URL = `${process.env.REACT_APP_API_URL}/media-rate`;
// const MEDIA_URL = `${process.env.REACT_APP_API_URL}/media/get-all`;
// const ADV_TYPE_URL = `${process.env.REACT_APP_API_URL}/quotation-advertising-type`;

// const defaultForm = {
//   media: "",
//   advertisingTypes: [], // Changed from advertisingType to advertisingTypes (array)
//   edition: "",
//   ratePerSquareMeter: "",
// };

// const Spinner = () => (
//   <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
// );

// const Toast = ({ message, type, onClose }) => {
//   useEffect(() => {
//     const t = setTimeout(onClose, 3500);
//     return () => clearTimeout(t);
//   }, []);
//   return (
//     <div
//       className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
//       ${
//         type === "success"
//           ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
//           : "bg-red-50 border border-red-200 text-red-800"
//       }`}
//     >
//       {type === "success" ? (
//         <RiCheckLine className="w-4 h-4 text-emerald-500" />
//       ) : (
//         <RiErrorWarningLine className="w-4 h-4 text-red-500" />
//       )}
//       <span>{message}</span>
//       <button
//         onClick={onClose}
//         className="ml-2 opacity-50 hover:opacity-100 transition"
//       >
//         <RiCloseLine className="w-4 h-4" />
//       </button>
//     </div>
//   );
// };

// const Modal = ({ title, onClose, children }) =>
//   createPortal(
//     <div className="fixed inset-0 z-40 flex items-center justify-center">
//       <div
//         className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
//         onClick={onClose}
//       />
//       <div className="relative z-10 w-full max-w-lg mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden">
//         <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
//           <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
//             {title}
//           </h2>
//           <button
//             onClick={onClose}
//             className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
//           >
//             <RiCloseLine className="w-5 h-5" />
//           </button>
//         </div>
//         <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
//           {children}
//         </div>
//       </div>
//     </div>,
//     document.body,
//   );

// const SelectField = ({
//   label,
//   name,
//   value,
//   onChange,
//   options,
//   placeholder,
//   Icon,
// }) => (
//   <div className="flex flex-col gap-1.5">
//     <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
//       {label}
//     </label>
//     <div className="relative">
//       {Icon && (
//         <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
//       )}
//       <select
//         name={name}
//         value={value}
//         onChange={onChange}
//         className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
//       >
//         <option value="">{placeholder || `Select ${label}`}</option>
//         {options.map((o) => (
//           <option key={o.value} value={o.value}>
//             {o.label}
//           </option>
//         ))}
//       </select>
//     </div>
//   </div>
// );

// // New Multi-Select Component for Advertising Types
// const MultiSelectField = ({
//   label,
//   name,
//   value,
//   onChange,
//   options,
//   placeholder,
//   Icon,
// }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const dropdownRef = React.useRef(null);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const toggleOption = (optionValue) => {
//     const newValue = value.includes(optionValue)
//       ? value.filter((v) => v !== optionValue)
//       : [...value, optionValue];
//     onChange({ target: { name, value: newValue } });
//   };

//   const removeOption = (optionValue) => {
//     onChange({
//       target: { name, value: value.filter((v) => v !== optionValue) },
//     });
//   };

//   const selectedLabels = value
//     .map((v) => {
//       const option = options.find((o) => o.value === v);
//       return option ? option.label : "";
//     })
//     .filter(Boolean);

//   return (
//     <div className="flex flex-col gap-1.5" ref={dropdownRef}>
//       <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
//         {label}
//       </label>
//       <div className="relative">
//         {Icon && (
//           <Icon className="absolute left-3 top-3 w-4 h-4 text-blue-300 pointer-events-none z-10" />
//         )}
//         <div
//           onClick={() => setIsOpen(!isOpen)}
//           className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 cursor-pointer min-h-[42px]"
//         >
//           {selectedLabels.length > 0 ? (
//             <div className="flex flex-wrap gap-1.5">
//               {selectedLabels.map((label, idx) => (
//                 <span
//                   key={idx}
//                   className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md text-xs"
//                 >
//                   {label}
//                   <button
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       const optionValue = value[idx];
//                       removeOption(optionValue);
//                     }}
//                     className="hover:text-blue-900"
//                   >
//                     <RiCloseLine className="w-3 h-3" />
//                   </button>
//                 </span>
//               ))}
//             </div>
//           ) : (
//             <span className="text-blue-300">
//               {placeholder || `Select ${label}`}
//             </span>
//           )}
//         </div>

//         {isOpen && (
//           <div className="absolute z-20 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
//             {options.map((option) => (
//               <label
//                 key={option.value}
//                 className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
//               >
//                 <input
//                   type="checkbox"
//                   checked={value.includes(option.value)}
//                   onChange={() => toggleOption(option.value)}
//                   className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
//                 />
//                 <span className="text-slate-700">{option.label}</span>
//               </label>
//             ))}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// const InputField = ({
//   label,
//   name,
//   value,
//   onChange,
//   type = "text",
//   placeholder,
//   Icon,
// }) => (
//   <div className="flex flex-col gap-1.5">
//     <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
//       {label}
//     </label>
//     <div className="relative">
//       {Icon && (
//         <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
//       )}
//       <input
//         type={type}
//         name={name}
//         value={value}
//         onChange={onChange}
//         placeholder={placeholder || label}
//         className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
//       />
//     </div>
//   </div>
// );

// export default function MediaRate() {
//   const [rates, setRates] = useState([]);
//   const [mediaList, setMediaList] = useState([]);
//   const [advTypes, setAdvTypes] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [actionLoading, setActionLoading] = useState(false);
//   const [modal, setModal] = useState(null);
//   const [selected, setSelected] = useState(null);
//   const [form, setForm] = useState(defaultForm);
//   const [toast, setToast] = useState(null);
//   const [search, setSearch] = useState("");

//   const notify = (message, type = "success") => setToast({ message, type });

//   const fetchRates = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.get(BASE_URL);
//       setRates(res.data?.data || res.data || []);
//     } catch {
//       notify("Failed to fetch rate cards", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchDropdowns = async () => {
//     try {
//       const [mRes, aRes] = await Promise.all([
//         axios.get(MEDIA_URL),
//         axios.get(ADV_TYPE_URL),
//       ]);
//       setMediaList(mRes.data?.data || mRes.data || []);
//       setAdvTypes(aRes.data?.data || aRes.data || []);
//     } catch {
//       notify("Failed to load dropdown data", "error");
//     }
//   };

//   useEffect(() => {
//     fetchRates();
//     fetchDropdowns();
//   }, []);

//   const mediaOptions = mediaList.map((m) => ({
//     value: m._id,
//     label: m.organisation || m.name || m._id,
//   }));

//   const advTypeOptions = advTypes.map((a) => ({
//     value: a._id,
//     label: a.name,
//   }));

//   const openCreate = () => {
//     setForm(defaultForm);
//     setModal("create");
//   };
//   const openEdit = (rate) => {
//     setSelected(rate);
//     setForm({
//       media: rate.media?._id || rate.media || "",
//       advertisingTypes: [
//         rate.advertisingType?._id || rate.advertisingType || "",
//       ], // Convert to array for edit
//       edition: rate.edition || "",
//       ratePerSquareMeter: rate.ratePerSquareMeter ?? "",
//     });
//     setModal("edit");
//   };
//   const openDelete = (rate) => {
//     setSelected(rate);
//     setModal("delete");
//   };
//   const closeModal = () => {
//     setModal(null);
//     setSelected(null);
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setForm((f) => ({ ...f, [name]: value }));
//   };

//   const validate = () => {
//     const { media, advertisingTypes, edition, ratePerSquareMeter } = form;
//     if (
//       !media ||
//       !advertisingTypes ||
//       advertisingTypes.length === 0 ||
//       !edition ||
//       ratePerSquareMeter === ""
//     ) {
//       notify(
//         "All fields are required. Please select at least one advertising type.",
//         "error",
//       );
//       return false;
//     }
//     if (ratePerSquareMeter <= 0) {
//       notify("Rate per square meter must be greater than 0", "error");
//       return false;
//     }
//     return true;
//   };

//   const handleCreate = async () => {
//     if (!validate()) return;
//     setActionLoading(true);
//     try {
//       // Using the bulk create endpoint
//       const response = await axios.post(`${BASE_URL}/bulk`, {
//         media: form.media,
//         advertisingTypes: form.advertisingTypes, // Send array of advertising types
//         edition: form.edition,
//         ratePerSquareMeter: Number(form.ratePerSquareMeter),
//       });

//       notify(
//         response.data?.message ||
//           `${response.data?.count || form.advertisingTypes.length} rate cards created successfully`,
//       );
//       fetchRates();
//       closeModal();
//     } catch (err) {
//       notify(
//         err?.response?.data?.message || "Failed to create rate cards",
//         "error",
//       );
//     } finally {
//       setActionLoading(false);
//     }
//   };

//   const handleUpdate = async () => {
//     if (!validate()) return;
//     setActionLoading(true);
//     try {
//       // For update, we still use the single update endpoint
//       // Note: This will update only the specific rate card
//       await axios.put(`${BASE_URL}/${selected._id}`, {
//         media: form.media,
//         advertisingType: form.advertisingTypes[0], // Take first item for single update
//         edition: form.edition,
//         ratePerSquareMeter: Number(form.ratePerSquareMeter),
//       });
//       notify("Rate card updated successfully");
//       fetchRates();
//       closeModal();
//     } catch (err) {
//       notify(
//         err?.response?.data?.message || "Failed to update rate card",
//         "error",
//       );
//     } finally {
//       setActionLoading(false);
//     }
//   };

//   const handleDelete = async () => {
//     setActionLoading(true);
//     try {
//       await axios.delete(`${BASE_URL}/${selected._id}`);
//       notify("Rate card deleted successfully");
//       fetchRates();
//       closeModal();
//     } catch (err) {
//       notify(
//         err?.response?.data?.message || "Failed to delete rate card",
//         "error",
//       );
//     } finally {
//       setActionLoading(false);
//     }
//   };

//   const filtered = rates.filter((r) => {
//     const mediaName = r.media?.organisation || r.media?.name || "";
//     const advName = r.advertisingType?.name || "";
//     const q = search.toLowerCase();
//     return (
//       mediaName.toLowerCase().includes(q) ||
//       advName.toLowerCase().includes(q) ||
//       (r.edition || "").toLowerCase().includes(q)
//     );
//   });

//   const tableHeaders = [
//     { label: "#" },
//     { label: "Media", Icon: RiTvLine },
//     { label: "Adv. Type", Icon: RiPriceTag3Line },
//     { label: "Edition", Icon: RiMapPinLine },
//     { label: "Rate / Sq. Meter (₹)", Icon: RiMoneyDollarCircleLine },
//     { label: "Created At", Icon: RiFileTextLine },
//     { label: "Actions" },
//   ];

//   // Rendered as a plain JSX expression (not a component) to prevent
//   // unmount/remount on every keystroke, which would steal focus.
//   const renderFormBody = () => (
//     <div className="flex flex-col gap-4">
//       <SelectField
//         label="Media"
//         name="media"
//         value={form.media}
//         onChange={handleChange}
//         options={mediaOptions}
//         placeholder="Select Media"
//         Icon={RiTvLine}
//       />
//       <MultiSelectField
//         label="Advertising Types (Select Multiple)"
//         name="advertisingTypes"
//         value={form.advertisingTypes}
//         onChange={handleChange}
//         options={advTypeOptions}
//         placeholder="Select one or more advertising types"
//         Icon={RiPriceTag3Line}
//       />
//       <InputField
//         label="Edition"
//         name="edition"
//         value={form.edition}
//         onChange={handleChange}
//         placeholder="e.g. Mumbai, Delhi, National"
//         Icon={RiMapPinLine}
//       />
//       <InputField
//         label="Rate Per Square Meter (₹)"
//         name="ratePerSquareMeter"
//         value={form.ratePerSquareMeter}
//         onChange={handleChange}
//         type="number"
//         placeholder="e.g. 5000"
//         Icon={RiMoneyDollarCircleLine}
//       />

//       {/* Show summary if multiple advertising types are selected */}
//       {form.advertisingTypes.length > 1 && (
//         <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
//           <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1">
//             <RiCheckboxCircleLine className="w-3.5 h-3.5" />
//             Will create {form.advertisingTypes.length} separate rate cards:
//           </p>
//           <p className="text-xs text-blue-600">
//             One for each selected advertising type with the same media, edition,
//             and rate
//           </p>
//         </div>
//       )}
//     </div>
//   );

//   return (
//     <div className="min-h-screen bg-white text-slate-800">
//       {/* Header */}
//       <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
//         <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
//           <div className="flex items-center gap-3">
//             <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
//               <RiMoneyDollarCircleLine className="w-5 h-5" />
//             </div>
//             <div>
//               <h1 className="text-base font-bold text-blue-800 tracking-tight">
//                 Media Rate Cards
//               </h1>
//               <p className="text-[11px] text-blue-400 tracking-widest uppercase">
//                 Rate Card Management
//               </p>
//             </div>
//           </div>

//           <div className="flex items-center gap-3">
//             <div className="relative">
//               <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
//               <input
//                 value={search}
//                 onChange={(e) => setSearch(e.target.value)}
//                 placeholder="Search rates..."
//                 className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-52 transition"
//               />
//             </div>

//             <button
//               onClick={fetchRates}
//               disabled={loading}
//               title="Refresh"
//               className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
//             >
//               <RiRefreshLine
//                 className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
//               />
//             </button>

//             <button
//               onClick={openCreate}
//               className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
//             >
//               <RiAddLine className="w-4 h-4" />
//               Add Rate Cards
//             </button>
//           </div>
//         </div>
//       </header>

//       {/* Table */}
//       <div className="w-full mx-auto px-6 pt-6 pb-10">
//         <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
//           <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
//             <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
//               <RiMoneyDollarCircleLine className="w-4 h-4 text-blue-500" />
//               All Rate Cards
//             </span>
//             <span className="text-xs text-blue-400">
//               {filtered.length} record{filtered.length !== 1 ? "s" : ""}
//             </span>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full text-sm">
//               <thead>
//                 <tr className="border-b border-blue-100 bg-blue-50/30">
//                   {tableHeaders.map(({ label, Icon }) => (
//                     <th key={label} className="px-4 py-3 text-left">
//                       <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
//                         {Icon && <Icon className="w-3.5 h-3.5" />}
//                         {label}
//                       </span>
//                     </th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 {loading ? (
//                   <tr>
//                     <td colSpan={7} className="py-20 text-center">
//                       <div className="flex flex-col items-center gap-3 text-blue-400">
//                         <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
//                         <span className="text-sm">Loading rate cards…</span>
//                       </div>
//                     </td>
//                   </tr>
//                 ) : filtered.length === 0 ? (
//                   <tr>
//                     <td colSpan={7} className="py-20 text-center">
//                       <div className="flex flex-col items-center gap-3">
//                         <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
//                           <RiMoneyDollarCircleLine className="w-7 h-7 text-blue-300" />
//                         </div>
//                         <span className="text-sm text-slate-400">
//                           {search
//                             ? "No rate cards match your search."
//                             : "No rate cards found. Add one to get started."}
//                         </span>
//                       </div>
//                     </td>
//                   </tr>
//                 ) : (
//                   filtered.map((rate, i) => (
//                     <tr
//                       key={rate._id || i}
//                       className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
//                     >
//                       <td className="px-4 py-3 text-slate-400 text-xs font-medium">
//                         {i + 1}
//                       </td>
//                       <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
//                         <div className="flex items-center gap-2">
//                           <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold shadow-sm shadow-blue-200 flex-shrink-0">
//                             {(rate.media?.organisation ||
//                               rate.media?.name ||
//                               "?")[0].toUpperCase()}
//                           </div>
//                           {rate.media?.organisation || rate.media?.name || "—"}
//                         </div>
//                       </td>
//                       <td className="px-4 py-3 whitespace-nowrap">
//                         <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-xs font-semibold text-blue-600">
//                           <RiPriceTag3Line className="w-3 h-3" />
//                           {rate.advertisingType?.name || "—"}
//                         </span>
//                       </td>
//                       <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
//                         {rate.edition || "—"}
//                       </td>
//                       <td className="px-4 py-3 whitespace-nowrap">
//                         <span className="font-semibold text-emerald-700">
//                           ₹
//                           {Number(rate.ratePerSquareMeter).toLocaleString(
//                             "en-IN",
//                           )}
//                         </span>
//                       </td>
//                       <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
//                         {rate.createdAt
//                           ? new Date(rate.createdAt).toLocaleDateString(
//                               "en-IN",
//                               {
//                                 day: "2-digit",
//                                 month: "short",
//                                 year: "numeric",
//                               },
//                             )
//                           : "—"}
//                       </td>
//                       <td className="px-4 py-3">
//                         <div className="flex gap-2">
//                           <button
//                             onClick={() => openEdit(rate)}
//                             className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
//                           >
//                             <RiEditLine className="w-3.5 h-3.5" /> Edit
//                           </button>
//                           <button
//                             onClick={() => openDelete(rate)}
//                             className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
//                           >
//                             <RiDeleteBinLine className="w-3.5 h-3.5" /> Delete
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                   ))
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>
//       </div>

//       {/* Create Modal */}
//       {modal === "create" && (
//         <Modal
//           title={
//             <>
//               <RiAddLine className="w-5 h-5 text-blue-500" /> Add Rate Cards
//               (Bulk)
//             </>
//           }
//           onClose={closeModal}
//         >
//           {renderFormBody()}
//           <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-blue-50">
//             <button
//               onClick={closeModal}
//               className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
//             >
//               <RiCloseLine className="w-4 h-4" /> Cancel
//             </button>
//             <button
//               onClick={handleCreate}
//               disabled={actionLoading}
//               className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
//             >
//               {actionLoading ? (
//                 <Spinner />
//               ) : (
//                 <RiCheckLine className="w-4 h-4" />
//               )}
//               Create{" "}
//               {form.advertisingTypes.length > 0
//                 ? `(${form.advertisingTypes.length})`
//                 : ""}{" "}
//               Rate Card{form.advertisingTypes.length !== 1 ? "s" : ""}
//             </button>
//           </div>
//         </Modal>
//       )}

//       {/* Edit Modal */}
//       {modal === "edit" && (
//         <Modal
//           title={
//             <>
//               <RiEditLine className="w-5 h-5 text-blue-500" /> Edit —{" "}
//               {selected?.media?.organisation ||
//                 selected?.media?.name ||
//                 "Rate Card"}
//             </>
//           }
//           onClose={closeModal}
//         >
//           {renderFormBody()}
//           <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-blue-50">
//             <button
//               onClick={closeModal}
//               className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
//             >
//               <RiCloseLine className="w-4 h-4" /> Cancel
//             </button>
//             <button
//               onClick={handleUpdate}
//               disabled={actionLoading}
//               className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
//             >
//               {actionLoading ? (
//                 <Spinner />
//               ) : (
//                 <RiCheckLine className="w-4 h-4" />
//               )}
//               Save Changes
//             </button>
//           </div>
//         </Modal>
//       )}

//       {/* Delete Modal */}
//       {modal === "delete" && (
//         <Modal
//           title={
//             <>
//               <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
//               Deletion
//             </>
//           }
//           onClose={closeModal}
//         >
//           <div className="text-center py-4">
//             <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
//               <RiErrorWarningLine className="w-8 h-8 text-red-500" />
//             </div>
//             <p className="text-slate-700 font-semibold mb-1 text-base">
//               Delete rate card for{" "}
//               <span className="text-red-500">
//                 {selected?.media?.organisation ||
//                   selected?.media?.name ||
//                   "this entry"}
//               </span>
//               ?
//             </p>
//             <p className="text-slate-400 text-sm mb-6">
//               This action cannot be undone. This rate card will be permanently
//               removed.
//             </p>
//             <div className="flex justify-center gap-3">
//               <button
//                 onClick={closeModal}
//                 className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
//               >
//                 <RiCloseLine className="w-4 h-4" /> Cancel
//               </button>
//               <button
//                 onClick={handleDelete}
//                 disabled={actionLoading}
//                 className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
//               >
//                 {actionLoading ? (
//                   <Spinner />
//                 ) : (
//                   <RiDeleteBinLine className="w-4 h-4" />
//                 )}
//                 Delete Rate
//               </button>
//             </div>
//           </div>
//         </Modal>
//       )}

//       {toast && <Toast {...toast} onClose={() => setToast(null)} />}
//     </div>
//   );
// }
